import { PropsWithChildren, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';

interface ModalBackdropProps {
  className?: string;
  onClick?: () => void;
  viewportCentered?: boolean;
}

export default function ModalBackdrop({ children, className = '', onClick, viewportCentered = false }: PropsWithChildren<ModalBackdropProps>) {
  const [zIndex, setZIndex] = useState<number | null>(null);
  const TOP_BLEED_PX = 96;

  // Lock scroll immediately using useLayoutEffect (runs synchronously after DOM updates but before paint)
  useLayoutEffect(() => {
    try { 
      lockScroll(); 
    } catch (_e) { 
      void _e; 
    }
  }, []);

  // Manage a simple stack counter on document.body so multiple ModalBackdrops
  // that mount at different times get increasing z-indexes and render above
  // earlier modals. We store the value on dataset.modalStackCount.
  useEffect(() => {
    try {
      const body = document.body;
      const cur = parseInt(body.dataset.modalStackCount || '0', 10) || 0;
      const next = cur + 1;
      body.dataset.modalStackCount = String(next);
      // base z-index 100000, step by 10 per stacked modal
      setZIndex(100000 + next * 10);
      return () => {
        try {
          const before = parseInt(body.dataset.modalStackCount || '0', 10) || 0;
          const newVal = Math.max(0, before - 1);
          if (newVal === 0) {
            delete body.dataset.modalStackCount;
          } else {
            body.dataset.modalStackCount = String(newVal);
          }
        } catch (_e) { void _e; }
      };
    } catch (_e) { void _e; }
     
  }, []);

  // Ensure we release the lock when the backdrop unmounts.
  // Delay unlock slightly to let any DOM mutations (e.g. list removals)
  // settle before restoring scroll. This reduces a quick "jump" flicker
  // where the browser clamps the restored position while content is still
  // changing.
  useEffect(() => {
    return () => {
      try {
        // small delay — 60ms is enough for most microtasks/reflows
        setTimeout(() => { try { unlockScroll(); } catch (_e) { void _e; } }, 60);
      } catch (_e) { void _e; }
    };
  }, []);

  const content = (
    <div
      className={`fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 ${className}`}
      onClick={onClick}
      style={{ 
        ...(zIndex ? { zIndex } : { zIndex: 100000 }),
        top: viewportCentered ? 0 : `-${TOP_BLEED_PX}px`,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        minHeight: viewportCentered ? '100dvh' : '100vh',
        height: viewportCentered ? '100dvh' : `calc(100dvh + ${TOP_BLEED_PX}px)`,
        margin: 0,
        position: 'fixed'
      }}
    >
      {children}
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(content, document.body);
  }

  return content;
}
